import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { AiApiKey, usd } from '../aiApi';
import { describeModel, MODELS, ModelSelection, sameSelection } from '../model';
import { TM } from '../tokens';
import { ArrowDownIcon } from '../icons';

// Compact model picker in the prompt toolbar. Two variants, one per generation
// path (see providers/config.MAGIC_BACKEND_ENABLED):
//
//  • ModelPicker (backend, default) — the user picks a (stored key, model)
//    pair, because the backend derives the provider from `api_key_id`. Options
//    come from the keys themselves (AiApiKey.models), so a model the backend
//    starts serving needs no frontend release.
//  • LegacyModelPicker (direct-only) — the old static MODELS list, kept for the
//    disabled key-in-browser path.
//
// `placement` controls where the dropdown opens — 'bottom-right' (default) for
// the header, 'top-left' when the picker sits in the bottom prompt toolbar.

type Placement = 'bottom-right' | 'top-left';

const dropdownStyle = (placement: Placement, width: number): CSSProperties => ({
  position: 'absolute',
  ...(placement === 'top-left' ? { bottom: 36, left: 0 } : { top: 36, right: 0 }),
  width,
  maxHeight: 320,
  overflowY: 'auto',
  padding: 4,
  background: '#fff',
  border: `1px solid ${TM.border}`,
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  zIndex: 60,
});

// Close-on-outside-click for a popover anchored to `ref`. `setOpen` must be the
// state setter (stable identity) so the listener isn't re-bound every render.
function useCloseOnOutside(
  open: boolean,
  ref: React.RefObject<HTMLElement>,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, ref, setOpen]);
}

export const ModelPicker: React.FC<{
  options: ModelSelection[];
  value: ModelSelection | null;
  onPick: (selection: ModelSelection) => void;
  // Keys behind the options — only for the group headers (which key, how much
  // budget is left on it). Rendering never depends on a key being found.
  keys: AiApiKey[];
  // Opens the key popover from the "no keys yet" placeholder row.
  onAddKey: () => void;
  placement?: Placement;
}> = ({ options, value, onPick, keys, onAddKey, placement = 'bottom-right' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useCloseOnOutside(open, ref, setOpen);

  const active = value ? describeModel(value.model, value.provider) : null;
  // Group by key only when there is more than one — a single key would just add
  // a redundant header above every row.
  const grouped = keys.length > 1;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          height: 32,
          padding: '0 10px',
          borderRadius: 6,
          border: `1px solid ${TM.border}`,
          background: '#fff',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: TM.text,
          maxWidth: 260,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            flex: 'none',
            borderRadius: '50%',
            background: value ? TM.green : TM.textFaint,
          }}
        />
        <span
          style={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {active ? active.title : 'No model'}
        </span>
        <span style={{ color: TM.textFaint, fontSize: 11, flex: 'none' }}>
          {active ? active.tag : 'add a key'}
        </span>
        <ArrowDownIcon size={10} />
      </button>

      {open && (
        <div style={dropdownStyle(placement, 320)}>
          {options.length === 0 ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onAddKey();
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '10px',
                background: 'transparent',
                border: 'none',
                borderRadius: 5,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: TM.text }}>Add an API key</span>
              <span style={{ fontSize: 10, color: TM.textFaint }}>
                Models appear here once a Gemini or OpenAI key is connected.
              </span>
            </button>
          ) : (
            keys.map((key) => {
              const keyOptions = options.filter((o) => o.apiKeyId === key.id);
              if (keyOptions.length === 0) return null;
              return (
                <div key={key.id}>
                  {grouped && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 6,
                        padding: '6px 10px 2px',
                        fontSize: 10,
                        fontWeight: 700,
                        color: TM.textFaint,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {key.name?.trim() || (key.provider === 'gemini' ? 'Gemini' : 'OpenAI')}
                      </span>
                      <span style={{ flex: 1 }} />
                      <span style={{ textTransform: 'none', letterSpacing: 0 }}>
                        {usd(key.user_remaining_usd)} left
                      </span>
                    </div>
                  )}
                  {keyOptions.map((option) => {
                    const info = describeModel(option.model, option.provider);
                    const selected = sameSelection(option, value);
                    return (
                      <button
                        key={`${option.apiKeyId}:${option.model}`}
                        type="button"
                        onClick={() => {
                          onPick(option);
                          setOpen(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 10px',
                          background: selected ? TM.blueWash : 'transparent',
                          border: 'none',
                          borderRadius: 5,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          <span style={{ fontSize: 12, fontWeight: 600, color: TM.text }}>
                            {info.title}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              color: TM.textFaint,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {option.model}
                          </span>
                        </span>
                        {!grouped && (
                          <span style={{ fontSize: 10, color: TM.textFaint, flex: 'none' }}>
                            {usd(key.user_remaining_usd)} left
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// DIRECT-ONLY (legacy): the static MODELS list, used while
// MAGIC_BACKEND_ENABLED is false. "Coming soon" models are disabled.
export const LegacyModelPicker: React.FC<{
  modelId: string;
  onPick: (id: string) => void;
  placement?: Placement;
}> = ({ modelId, onPick, placement = 'bottom-right' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const model = MODELS.find((m) => m.id === modelId) ?? MODELS[0];
  useCloseOnOutside(open, ref, setOpen);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          height: 32,
          padding: '0 10px',
          borderRadius: 6,
          border: `1px solid ${TM.border}`,
          background: '#fff',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: TM.text,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: TM.green }} />
        <span style={{ fontWeight: 600 }}>{model.name}</span>
        <span style={{ color: TM.textFaint, fontSize: 11 }}>{model.tag}</span>
        <ArrowDownIcon size={10} />
      </button>
      {open && (
        <div style={dropdownStyle(placement, 280)}>
          {MODELS.map((m) => (
            <button
              key={m.id}
              type="button"
              disabled={m.disabled}
              onClick={() => {
                if (!m.disabled) {
                  onPick(m.id);
                  setOpen(false);
                }
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                background: model.id === m.id ? TM.blueWash : 'transparent',
                border: 'none',
                borderRadius: 5,
                cursor: m.disabled ? 'not-allowed' : 'pointer',
                opacity: m.disabled ? 0.5 : 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: TM.text }}>{m.name}</span>
              <span style={{ fontSize: 10, color: TM.textFaint }}>{m.apiName ?? m.tag}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
